import React from 'react';
import { themed } from '../../ui/theme';

/**
 * Regra de cor dos cartões (vale para todos os perfis):
 * - warning  (âmbar)    → "é a sua vez": algo espera ação deste usuário;
 * - critical (vermelho) → problema: atraso, vencimento, item parado;
 * - progress (azul)     → em andamento, sem ação imediata;
 * - success  (verde)    → concluído;
 * - neutral  (cinza)    → contagem informativa.
 * Valor zero sempre aparece em cinza: zero pendência não é alarme.
 */
export type WorkspaceMetricTone = 'critical' | 'warning' | 'progress' | 'success' | 'neutral';

export type WorkspaceMetric = {
  label: string;
  value: React.ReactNode;
  tone?: WorkspaceMetricTone;
  detail?: string;
};

type WorkspaceOverviewProps = {
  profile: string;
  description?: string;
  metrics?: WorkspaceMetric[];
  updatedLabel?: string;
  loading?: boolean;
};

const toneClasses: Record<WorkspaceMetricTone, string> = {
  critical: 'bg-red-500',
  warning: 'bg-amber-400',
  progress: 'bg-sky-500',
  success: 'bg-emerald-400',
  neutral: 'bg-slate-400',
};

// O número só ganha cor quando pede ação ou indica problema.
const valueToneClasses: Record<WorkspaceMetricTone, string> = {
  critical: 'text-danger-text',
  warning: 'text-warning-text',
  progress: 'text-fg',
  success: 'text-fg',
  neutral: 'text-fg',
};

const isZero = (value: React.ReactNode) => value === 0 || value === '0';

const metricGridClasses: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-1 sm:grid-cols-3',
  4: 'grid-cols-2 md:grid-cols-4',
  5: 'grid-cols-2 md:grid-cols-5',
};

const WorkspaceOverview: React.FC<WorkspaceOverviewProps> = ({
  profile,
  description = 'Fila única, ordenada por prazo e impacto.',
  metrics = [],
  updatedLabel = 'Atualizado agora',
  loading = false,
}) => {
  const surfaceClass = themed({
    minimal: 'rounded-lg border border-line bg-surface',
    saas: 'rounded-xl border border-line bg-surface shadow-sm',
  });
  const gridClass = metricGridClasses[Math.min(metrics.length, 5)] ?? 'grid-cols-2 md:grid-cols-4';

  return (
    <section aria-labelledby="workspace-title" className="space-y-2">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
        <div>
          <h1 id="workspace-title" className="text-lg font-semibold tracking-tight text-fg">
            Área de trabalho: {profile}
          </h1>
          <p className="mt-0.5 text-xs text-fg-subtle">{description}</p>
        </div>
        <span className="text-[11px] text-fg-subtle">{updatedLabel}</span>
      </div>

      {metrics.length > 0 && (
        <div className={`${surfaceClass} grid ${gridClass} gap-px overflow-hidden !bg-line`} aria-label="Resumo da área de trabalho">
          {metrics.map((metric, index) => {
            const zero = loading || isZero(metric.value);
            const tone = zero ? 'neutral' : (metric.tone ?? 'neutral');
            // Divisórias pelo gap da grade (funciona com 1, 2 ou 4 colunas). Em número ímpar de
            // cartões na grade de 2 colunas, o último ocupa a linha inteira.
            const ultimoSozinho = metrics.length % 2 === 1 && metrics.length > 3 && index === metrics.length - 1;
            return (
              <div
                key={metric.label}
                className={`min-w-0 bg-surface px-4 py-3 ${ultimoSozinho ? 'col-span-2 md:col-span-1' : ''}`}
              >
                <div className="flex items-center gap-1.5 text-xs font-medium text-fg-muted">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${toneClasses[tone]}`} />
                  <span className="truncate">{metric.label}</span>
                </div>
                <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <strong
                    className={`text-xl font-semibold leading-tight tabular-nums [overflow-wrap:anywhere] sm:text-2xl ${
                      zero ? 'text-fg-subtle' : valueToneClasses[tone]
                    }`}
                  >
                    {loading ? '—' : metric.value}
                  </strong>
                  {metric.detail && (
                    <span className="text-xs text-fg-subtle">{metric.detail}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

export default WorkspaceOverview;
